import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import type { GameDetail, InviteResponse, LobbyResponse, MatchmakingMode, VariantSummary } from '../api/types';
import { useApp } from '../app/AppContext';
import { config } from '../config';
import { useI18n } from '../i18n';
import type { StringKey } from '../i18n/strings';

const MODES: MatchmakingMode[] = ['random', 'invite', 'vs_ai', 'direct'];

function variantLabelKey(variantKey: string): StringKey | null {
  switch (variantKey) {
    case 'rps-bo1':
      return 'lobby.variant.rps-bo1';
    case 'rps-bo3':
      return 'lobby.variant.rps-bo3';
    case 'rps-bo5':
      return 'lobby.variant.rps-bo5';
    case 'rps-first-to-5':
      return 'lobby.variant.rps-first-to-5';
    default:
      return null;
  }
}

export function LobbyPage() {
  const { t } = useI18n();
  const { api, guestState, guestError, retryGuest, onMessage } = useApp();
  const navigate = useNavigate();

  const [game, setGame] = useState<GameDetail | null>(null);
  const [lobby, setLobby] = useState<LobbyResponse | null>(null);
  const [variantKey, setVariantKey] = useState<string | null>(null);
  const [mode, setMode] = useState<MatchmakingMode>('random');
  const [aiProfile, setAiProfile] = useState('nash');
  const [opponentId, setOpponentId] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [invite, setInvite] = useState<InviteResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [queueStartedAt, setQueueStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- 載入目錄 + 大廳狀態 ------------------------------------------------
  useEffect(() => {
    const ac = new AbortController();
    api
      .getGame(config.gameKey, ac.signal)
      .then((g) => {
        setGame(g);
        setVariantKey((prev) => prev ?? g.variants.find((v) => v.published)?.variant_key ?? null);
      })
      .catch(() => {
        /* 目錄拿不到不致命：大廳仍可用邀請碼加入 */
      });
    return () => ac.abort();
  }, [api]);

  useEffect(() => {
    if (guestState !== 'ready') return;
    let cancelled = false;
    const load = () => {
      api
        .getLobby(config.gameKey)
        .then((l) => {
          if (!cancelled) setLobby(l);
        })
        .catch(() => undefined);
    };
    load();
    const id = setInterval(load, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [api, guestState]);

  // --- 排隊：WS 是主通道，輪詢是降級路徑（C02 §4） -------------------------
  const goToMatch = useCallback(
    (matchId: string) => {
      setTicketId(null);
      setQueueStartedAt(null);
      navigate(`/m/${encodeURIComponent(matchId)}`);
    },
    [navigate],
  );

  useEffect(() => {
    if (!ticketId) return;
    return onMessage((msg) => {
      if (msg.op !== 'event') return;
      // 媒合成功的事件形狀：`match_created` 帶 match_id。
      if (msg.event_type === 'match_created' || msg.event_type === 'match_started') {
        if (msg.match_id) goToMatch(msg.match_id);
      }
    });
  }, [ticketId, onMessage, goToMatch]);

  useEffect(() => {
    if (!ticketId) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => {
      api
        .getTicket(ticketId)
        .then((s) => {
          if (s.status === 'matched' && s.match_id) goToMatch(s.match_id);
          if (s.status === 'expired' || s.status === 'cancelled') {
            setTicketId(null);
            setQueueStartedAt(null);
          }
        })
        .catch(() => undefined);
    }, 1500);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [ticketId, api, goToMatch]);

  useEffect(() => {
    if (!queueStartedAt) return;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - queueStartedAt) / 1000)), 500);
    return () => clearInterval(id);
  }, [queueStartedAt]);

  // --- 動作 ---------------------------------------------------------------
  const startQueue = useCallback(async () => {
    if (!variantKey) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'invite') {
        const resp = await api.createInvite(config.gameKey, variantKey);
        setInvite(resp);
        setCopied(false);
        return;
      }
      const ticket = await api.enqueue(config.gameKey, {
        variant_key: variantKey,
        mode,
        opponent_subject_id: mode === 'direct' ? opponentId.trim() || null : null,
        ai_profile: mode === 'vs_ai' ? aiProfile : null,
      });
      setTicketId(ticket.ticket_id);
      setQueueStartedAt(Date.now());
    } catch (e) {
      setError(describeError(e, t));
    } finally {
      setBusy(false);
    }
  }, [api, variantKey, mode, opponentId, aiProfile, t]);

  const cancelQueue = useCallback(async () => {
    if (!ticketId) return;
    try {
      await api.cancelQueue(ticketId);
    } catch {
      /* 取消失敗就讓它自然過期 */
    }
    setTicketId(null);
    setQueueStartedAt(null);
  }, [api, ticketId]);

  const joinByCode = useCallback(async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await api.joinByCode(code);
      goToMatch(resp.match_id);
    } catch (e) {
      setError(describeError(e, t));
    } finally {
      setBusy(false);
    }
  }, [api, joinCode, goToMatch, t]);

  const variants = useMemo(() => (game?.variants ?? []).filter((v) => v.published), [game]);
  const queuedFor = (v: VariantSummary) => lobby?.variants.find((x) => x.variant_key === v.variant_key);

  // --- 匿名身分還沒好 -----------------------------------------------------
  if (guestState === 'creating' || guestState === 'idle') {
    return <p className="muted">{t('guest.creating')}</p>;
  }
  if (guestState === 'failed') {
    return (
      <div className="notice notice--error stack stack--tight">
        <strong>{t('guest.failed')}</strong>
        <span className="faint">{guestError}</span>
        <button type="button" className="btn btn--sm" onClick={retryGuest}>
          {t('guest.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="stack">
      {lobby?.my_active_match ? (
        <div className="notice spread">
          <span>{t('lobby.resume')}</span>
          <button type="button" className="btn btn--primary btn--sm" onClick={() => goToMatch(lobby.my_active_match!)}>
            {t('lobby.resumeAction')}
          </button>
        </div>
      ) : null}

      <section className="stack stack--tight">
        <div className="spread">
          <h1 className="h1">{t('lobby.title')}</h1>
          <span className="badge">
            {t('lobby.online')} {lobby?.online_count ?? '—'}
          </span>
        </div>
        <p className="faint">{t('lobby.guestNotice')}</p>
      </section>

      <section className="stack stack--tight" aria-label={t('lobby.title')}>
        <div className="variant-grid">
          {variants.map((v) => {
            const labelKey = variantLabelKey(v.variant_key);
            const q = queuedFor(v);
            return (
              <button
                key={v.variant_key}
                type="button"
                className="variant-card"
                aria-pressed={variantKey === v.variant_key}
                onClick={() => setVariantKey(v.variant_key)}
              >
                <strong className="h3">{labelKey ? t(labelKey) : v.display_name}</strong>
                <span className="faint">
                  {v.config?.rounds_to_win ?? '?'} 勝制 · {(v.config?.move_timeout_ms ?? 10000) / 1000}s / 回合
                </span>
                <span className="faint">
                  {t('lobby.queued')} {q?.queued ?? 0} · {t('lobby.openTables')} {q?.open_tables ?? 0} ·{' '}
                  {t('lobby.online')} {v.online_players ?? 0}
                </span>
              </button>
            );
          })}
          {variants.length === 0 ? <p className="muted">{t('common.loading')}</p> : null}
        </div>
      </section>

      <section className="stack stack--tight" aria-label={t('lobby.mode.title')}>
        <h2 className="h2">{t('lobby.mode.title')}</h2>
        <div className="mode-list">
          {MODES.map((m) => (
            <button key={m} type="button" className="mode-card" aria-pressed={mode === m} onClick={() => setMode(m)}>
              <strong>{t(`lobby.mode.${m}` as StringKey)}</strong>
              <span className="faint">{t(`lobby.mode.${m}.hint` as StringKey)}</span>
            </button>
          ))}
        </div>

        {mode === 'vs_ai' ? (
          <div className="field">
            <label htmlFor="ai-profile">{t('lobby.aiProfile')}</label>
            <select id="ai-profile" className="input" value={aiProfile} onChange={(e) => setAiProfile(e.target.value)}>
              <option value="nash">nash</option>
              <option value="exploiter-v1">exploiter-v1</option>
            </select>
          </div>
        ) : null}

        {mode === 'direct' ? (
          <div className="field">
            <label htmlFor="opponent-id">{t('lobby.opponentId')}</label>
            <input
              id="opponent-id"
              className="input"
              value={opponentId}
              onChange={(e) => setOpponentId(e.target.value)}
              placeholder="player:01J…"
            />
            {/* C03 §2：guest 不能被指名對決 —— 後端會擋，這裡先提醒。 */}
            <span className="faint">指名對決需要雙方都是登入玩家（C03 §2）。</span>
          </div>
        ) : null}
      </section>

      {error ? <div className="notice notice--error">{error}</div> : null}

      <section className="stack stack--tight">
        {ticketId ? (
          <div className="spread">
            <span aria-live="polite">
              {t('lobby.queueing')} · {t('lobby.queuePosition', { seconds: elapsed })}
            </span>
            <button type="button" className="btn btn--sm" onClick={() => void cancelQueue()}>
              {t('lobby.cancel')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={!variantKey || busy || (mode === 'direct' && !opponentId.trim())}
            onClick={() => void startQueue()}
          >
            {mode === 'invite' ? t('invite.create') : t('lobby.start')}
          </button>
        )}

        {invite ? (
          <div className="card card--flat stack stack--tight">
            <div className="spread">
              <span className="faint">{t('invite.code')}</span>
              <strong className="h2">{invite.invite_code}</strong>
            </div>
            <span className="faint">{t('invite.link')}</span>
            {/* C03 §5：分享連結用編譯期的 PUBLIC_BASE_URL，不用 window.location。
                後端也會回一個 join_url；兩者應相同，我們顯示後端那份以免不一致。 */}
            <code className="mono">{invite.join_url}</code>
            <div className="row">
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => {
                  void navigator.clipboard?.writeText(invite.join_url).then(() => setCopied(true));
                }}
              >
                {copied ? t('invite.copied') : t('invite.copy')}
              </button>
              <span className="faint">{t('invite.waiting')}</span>
            </div>
          </div>
        ) : null}
      </section>

      <section className="stack stack--tight">
        <h2 className="h2">{t('invite.join')}</h2>
        <div className="row">
          <input
            className="input"
            style={{ maxWidth: '14rem' }}
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            placeholder={t('invite.joinPlaceholder')}
            aria-label={t('invite.join')}
            maxLength={12}
          />
          <button type="button" className="btn" disabled={busy || !joinCode.trim()} onClick={() => void joinByCode()}>
            {busy ? t('invite.joining') : t('invite.join')}
          </button>
        </div>
      </section>
    </div>
  );
}

export function describeError(e: unknown, t: (k: StringKey) => string): string {
  if (e instanceof ApiError) {
    if (e.status === 429) return t('error.rateLimited');
    if (e.status === 404) return t('error.notFound');
    if (e.code === 'NETWORK_ERROR') return t('error.network');
    return `${e.code}: ${e.message}`;
  }
  return t('error.generic');
}
